import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const AttachmentViewer: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    // Extract attachment path from URL (everything after /attachments)
    const attachmentPath = location.pathname;
    if (attachmentPath) {
      // Create the API URL internally
      const baseUrl = import.meta.env.VITE_API_LOCAL_URL as string;
      const apiUrl = `${baseUrl}${attachmentPath}`;

      // Fetch the PDF as blob to avoid showing API URL
      void fetchPdfBlob(apiUrl);
    } else {
      setError('No attachment path provided');
      setLoading(false);
    }
  }, [location.pathname]);

  const fetchPdfBlob = async (url: string) => {
    try {
      setLoading(true);
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPdfUrl(blobUrl);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching PDF:', err);
      setError('Failed to load PDF document');
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Clean up blob URL to prevent memory leaks
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }
    void navigate('/search');
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        bgcolor="background.default"
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading PDF...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        bgcolor="background.default"
      >
        <Typography variant="h6" color="error" gutterBottom>
          {error}
        </Typography>
        <IconButton onClick={handleClose} sx={{ mt: 2 }}>
          <ArrowBackIcon />
          <Typography variant="button" sx={{ ml: 1 }}>
            Go Back
          </Typography>
        </IconButton>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>

      {/* PDF Viewer */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            width="100%"
            height="100%"
            style={{ border: 'none' }}
            title="PDF Viewer"
          />
        )}
      </Box>
    </Box>
  );
};

export default AttachmentViewer;
